/**
 * TrustIdentitySection — identity block for the Trust person dialog.
 *
 * Entity-specific analogue of `IndividualIdentitySection`. Trust sub-type is
 * manually selected (Revocable / Irrevocable / QIT / SelfSettledSNT) — unlike
 * Individual, where customerType auto-derives from DOB + citizenship. When the
 * officer changes sub-type, the section pushes a derived-fields patch (call
 * class, TIN code default, cddExempt, occupation, cifConvention, etc.) into
 * local state via onChange.
 *
 * Fields:
 *   - Trust Name (Core Director Field 6 / `firstName`)
 *   - Trustee/Executor Name 2 (Field 8 / `name2`)
 *   - Trust Sub-type dropdown (drives customerType + all derived fields)
 *   - Trust Tax ID (SSN or EIN; auto-toggled by sub-type default tinCode,
 *     officer can override tinCode manually)
 *   - Trust Agreement Date (Field 257 + duplicated to Field 30 DOB via extra.idIssueDate / person.dob)
 *   - Trust State — governing law (Field 256 / extra.idState)
 *   - Governing Trustee Count (for trustee repeater sizing hint)
 *
 * OMITS:
 *   - DOB as a distinct input (Trust Agreement Date IS DOB per §13c)
 *   - Gender, Insider code, Backup withholding (not applicable to entities)
 *
 * Per the card's Open Questions A/B/C/D, Field 254/255/256/258 defaults are
 * conservative; the CIP section owns those — this section only writes the
 * fields listed above.
 */

import { useCallback, useEffect } from 'react';
import { Pill } from '@/components/primitives';
import { formatSsn } from '@/utils/ssn';
import type { RaoPerson, RaoTinCode } from '../../../types';
import type { DerivationResult } from './useTrustDerivation';
import { buildDerivedTrustPatch } from './useTrustDerivation';
import {
  TRUST_SUB_TYPES,
  TRUSTEE_COUNT_OPTIONS,
  GOVERNING_LAW_STATES,
  buildTrustTitlingSuffix,
  type TrustSubType,
} from './trust-config';

// -----------------------------------------------------------------------------
// Shared leaf styles (mirror IndividualIdentitySection for visual consistency)
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers (trustAgreementDate + idState live on person.extra)
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface TrustIdentitySectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  derivation: DerivationResult;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function derivationExplain(d: DerivationResult): string {
  switch (d.derivedFrom) {
    case 'subType':  return `from sub-type: ${d.subTypeOption.label}`;
    case 'baseline': return 'default baseline (no selection yet)';
    default:         return 'derived';
  }
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function TrustIdentitySection({
  person,
  onChange,
  mode,
  isChanged,
  derivation,
  readOnly = false,
}: TrustIdentitySectionProps): JSX.Element {
  const isExisting = mode === 'existing';
  const noTrustTin = person.tinCode === 'N';
  const ic = isChanged ?? ((_: string) => false);

  // Trust agreement date is stored in two places per §13c:
  //   - person.dob (Core Director Field 30)
  //   - person.extra.idIssueDate (Field 257)
  // We read/write both together so they can't drift.
  const agreementDate = person.dob ?? readExtra(person, 'idIssueDate');
  const trustState = readExtra(person, 'idState');
  const trusteeCount = readExtra(person, 'governingTrusteeCount');
  const selectedSubType = derivation.trustSubType;

  // Publish the derived customerType + coarse flags whenever derivation moves
  // — parity with IndividualIdentitySection's effect.
  useEffect(() => {
    if (person.customerType !== derivation.customerType) {
      onChange({ customerType: derivation.customerType as RaoPerson['customerType'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivation.customerType]);

  // Handle sub-type picker change — push a full derived patch (call class,
  // tin code default, cddExempt, occupation, cifConvention, etc.) so the
  // officer isn't forced to edit 7 fields by hand after flipping the type.
  const handleSubTypeChange = useCallback(
    (nextSubType: TrustSubType): void => {
      const patch = buildDerivedTrustPatch(nextSubType, person.extra);
      onChange(patch);
    },
    [onChange, person.extra],
  );

  const handleAgreementDateChange = useCallback(
    (iso: string): void => {
      // Write into both dob (Field 30) and extra.idIssueDate (Field 257)
      // in a single patch so they stay in sync.
      onChange({
        dob: iso,
        extra: { ...(person.extra ?? {}), idIssueDate: iso },
      });
    },
    [onChange, person.extra],
  );

  // Compute the UAD titling display hint.
  const titlingHint = buildTrustTitlingSuffix(selectedSubType, agreementDate);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Entity-type chip — mirrors IndividualIdentitySection's read-only pill */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--info-line)',
          borderRadius: 6,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
          Entity Type:
        </span>
        <Pill variant="info">{derivation.customerType}</Pill>
        <Pill variant="muted">{derivation.subTypeOption.label}</Pill>
        <span
          style={{ fontSize: 11, color: 'var(--ink-600)' }}
          title={`Auto-derived: ${derivationExplain(derivation)}`}
        >
          ({derivationExplain(derivation)})
        </span>
        {derivation.promotedFrom && (
          <span style={{ fontSize: 11, color: 'var(--pcb-blue)', marginLeft: 'auto' }}>
            updated from &lsquo;{derivation.promotedFrom}&rsquo;
          </span>
        )}
      </div>

      {/* Supervisor-approval gate — mirrors IndividualIdentitySection */}
      {derivation.requiresSupervisorApproval && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Supervisor/Manager approval required.</strong>{' '}
          {derivation.subTypeOption.requiresManagerApproval
            ? `${derivation.subTypeOption.label} requires Manager-role approval before CIF commit.`
            : `Sub-type changed from '${derivation.promotedFrom}' — approver must confirm.`}
        </div>
      )}

      {/* Risk-management banner (Irrevocable only) */}
      {derivation.requiresRiskReview && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Risk Management review required.</strong> Full trust agreement must be submitted
          to Risk Management <em>before</em> opening any account. Attach the Risk approval in the
          document slots section below.
        </div>
      )}

      {/* SelfSettledSNT grantor-cannot-sign banner */}
      {derivation.grantorCannotSign && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Grantor cannot be a signer.</strong> Self-Settled Special Needs Trusts prohibit
          the grantor from being added to the account as an authorized signer.
        </div>
      )}

      {/* QIT no-debit-card banner */}
      {derivation.blockDebitCards && (
        <div
          role="status"
          style={{
            padding: '8px 12px',
            background: 'var(--info-bg)',
            border: '1px solid var(--pcb-blue)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-900)',
          }}
        >
          Qualified Income Trust — <strong>debit cards are not permitted</strong> on QIT accounts.
        </div>
      )}

      {/* Trust Sub-type picker */}
      <div>
        <label style={labelStyle} htmlFor="rao-trust-subtype">Trust sub-type</label>
        <select
          id="rao-trust-subtype"
          value={selectedSubType}
          onChange={(e) => handleSubTypeChange(e.currentTarget.value as TrustSubType)}
          disabled={readOnly}
          style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('customerType'))}
          title="Trust sub-type — drives call class, tin code default, and risk-review requirements"
        >
          {TRUST_SUB_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4, fontStyle: 'italic' }}>
          {derivation.subTypeOption.helpText}
        </div>
      </div>

      {/* Trust Name row (primary + secondary trustee/executor name) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-name">
            Trust Name (Field 6) <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
          </label>
          <input
            id="rao-trust-name"
            type="text"
            value={person.firstName ?? ''}
            onChange={(e) => onChange({ firstName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('firstName'))}
            title="Full legal trust name (per the trust agreement)"
            maxLength={40}
            placeholder="John A Doe Revocable Living Trust"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-name2">
            Secondary / Trustee Name (Field 8)
          </label>
          <input
            id="rao-trust-name2"
            type="text"
            value={person.name2 ?? ''}
            onChange={(e) => onChange({ name2: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('name2'))}
            title="Optional — secondary trustee or executor name shown on sig card"
            maxLength={40}
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Titling-convention hint */}
      {titlingHint && (
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: -4, fontStyle: 'italic' }}>
          Titling hint — signature card name will read: &ldquo;{(person.firstName ?? '').trim() || '[Trust Name]'} {titlingHint}&rdquo;
        </div>
      )}

      {/* Tax ID / TIN code / Agreement date row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-taxid">
            Trust Tax ID ({derivation.subTypeOption.tinCodeDefault === 'E' ? 'EIN' : 'SSN'}){' '}
            {person.taxId && person.taxId.length === 9 && (
              <span style={{ color: 'var(--ink-600)', fontWeight: 400 }}>
                ({formatSsn(person.taxId)})
              </span>
            )}
          </label>
          <input
            id="rao-trust-taxid"
            type="text"
            value={noTrustTin ? '' : (person.taxId ?? '')}
            onChange={(e) =>
              onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })
            }
            readOnly={isExisting || readOnly || noTrustTin}
            style={changedFieldStyle(isExisting || readOnly || noTrustTin ? readOnlyStyle : inputStyle, ic('taxId'))}
            maxLength={9}
            placeholder={derivation.subTypeOption.tinCodeDefault === 'E' ? '123456789 (EIN)' : '123456789 (SSN)'}
            title="Trust Tax ID (SSN for Revocable/QIT grantor; EIN for Irrevocable/SelfSettledSNT)"
          />
          {/* Bill 2026-10-14: no SSN or EIN of its own → TIN code N; Core Director is searched by the
              trust name first, then the trust gets a bank-assigned customer number. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, color: 'var(--ink-800)' }}>
            <input
              type="checkbox"
              checked={noTrustTin}
              disabled={isExisting || readOnly}
              onChange={(e) => onChange(e.currentTarget.checked
                ? { tinCode: 'N', taxId: '' }
                : { tinCode: derivation.subTypeOption.tinCodeDefault })}
            />
            No SSN / EIN
          </label>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-tin">TIN code</label>
          <select
            id="rao-trust-tin"
            value={person.tinCode ?? derivation.subTypeOption.tinCodeDefault}
            onChange={(e) => onChange({ tinCode: e.currentTarget.value as RaoTinCode })}
            disabled={readOnly || noTrustTin}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('tinCode'))}
            title="TIN code — officer may override the sub-type default"
          >
            <option value="S">S · SSN (grantor)</option>
            <option value="E">E · EIN (trust)</option>
            {noTrustTin && <option value="N">N · No tax ID number</option>}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-agreement-date">
            Trust Agreement Date (Field 257 / 30) <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
          </label>
          <input
            id="rao-trust-agreement-date"
            type="date"
            value={agreementDate}
            onChange={(e) => handleAgreementDateChange(e.currentTarget.value)}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('dob'))}
            title="Trust agreement execution date — writes to both DOB (Field 30) and ID Issue Date (Field 257)"
          />
        </div>
      </div>

      {/* Trust State / Governing trustee count row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-state">
            Trust State — governing law (Field 256)
          </label>
          <select
            id="rao-trust-state"
            value={trustState}
            onChange={(e) => onChange(patchExtra(person, { idState: e.currentTarget.value }))}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idState'))}
            title="State where the trust was executed (defaults to branch state at commit time)"
          >
            <option value="">— Select —</option>
            {GOVERNING_LAW_STATES.map((s) => (
              <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-trust-trustee-count">Governing trustee count</label>
          <select
            id="rao-trust-trustee-count"
            value={trusteeCount}
            onChange={(e) => onChange(patchExtra(person, { governingTrusteeCount: e.currentTarget.value }))}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('governingTrusteeCount'))}
            title="How many governing trustees are named in the trust agreement"
          >
            <option value="">— Select —</option>
            {TRUSTEE_COUNT_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
